<template>
  <q-page class="certificacion-page">
    <div class="cert-container">
      <!-- Loading state -->
      <div v-if="loading" class="cert-loading">
        <q-skeleton type="rect" width="100%" height="40px" dark class="q-mb-md" />
        <q-skeleton type="rect" width="100%" height="200px" dark class="q-mb-md" />
        <q-skeleton type="rect" width="100%" height="200px" dark />
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="cert-empty">
        <q-icon name="error_outline" size="48px" :style="{ color: 'var(--oa-danger)' }" />
        <p class="empty-text">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          :style="{ background: 'var(--oa-gold-full)', color: 'var(--color-marble-cream)', fontWeight: '600', borderRadius: '8px' }"
          @click="loadCareerData"
        />
      </div>

      <!-- Main content -->
      <template v-else>
        <!-- Header -->
        <div class="cert-header">
          <div>
            <h1 class="cert-title">Certificacion</h1>
            <p class="cert-subtitle">Dual Gate — Examen Teorico + Clase Especial</p>
          </div>
          <div class="cert-header-right">
            <div class="cert-header-stats">
              <span><strong class="stat-value">{{ certCount }}</strong> certs</span>
              <span><strong class="stat-value">{{ passedExams }}/{{ exams.length }}</strong> exams</span>
              <span class="stat-xp">{{ xp.toLocaleString() }} AURA</span>
            </div>
            <router-link to="/ascenso" class="ascenso-link">
              <q-icon name="map" size="12px" />
              Ascenso
            </router-link>
          </div>
        </div>

        <!-- Career Tracks -->
        <div class="cert-section">
          <h2 class="cert-section-title">Camino de Certificacion</h2>
          <div class="tracks-grid">
            <div
              v-for="(track, trackKey) in CAREER_TRACKS"
              :key="trackKey"
              class="track-card"
            >
              <div class="track-header">
                <span class="track-label">{{ track.label }}</span>
                <span v-if="track.prereq" class="track-prereq">
                  Requiere {{ track.prereq }}
                </span>
              </div>
              <div class="track-path">
                <template v-for="(level, i) in track.path" :key="level.key">
                  <div class="track-step">
                    <div
                      class="track-step-icon"
                      :class="{ 'step-pulse': targetLevel === level.key }"
                      :style="getStepStyle(level.key)"
                    >
                      <q-icon
                        v-if="isCertified(level.key)"
                        name="check_circle"
                        size="20px"
                      />
                      <span v-else class="step-short">
                        {{ LEVEL_SHORT[level.key] || level.key.toUpperCase() }}
                      </span>
                    </div>
                    <p
                      class="track-step-label"
                      :style="{ color: getStepLabelColor(level.key) }"
                    >
                      {{ LEVEL_LABELS[level.key] || level.key }}
                    </p>
                    <p class="track-step-pct">{{ level.passingPct }}%</p>
                  </div>
                  <q-icon
                    v-if="i < track.path.length - 1 && !track.standalone"
                    name="chevron_right"
                    size="14px"
                    class="track-chevron"
                    :style="{ color: isCertified(level.key) ? getTierColor(level.key) : 'var(--oa-border)' }"
                  />
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Current target progress -->
        <div v-if="targetLevel && courseCompletion" class="cert-section">
          <h2 class="cert-section-title">
            Tu progreso hacia {{ LEVEL_LABELS[targetLevel] || targetLevel }}
          </h2>
          <div
            class="progress-card"
            :style="{
              border: '1px solid ' + getTierColor(targetLevel),
              boxShadow: '0 0 20px color-mix(in srgb, ' + getTierColor(targetLevel) + ' 8%, transparent)',
            }"
          >
            <!-- Gate 1: Teoria -->
            <div class="progress-gate">
              <div class="progress-gate-header">
                <div class="progress-gate-title">
                  <q-icon name="shield" size="16px" :style="{ color: getTierColor(targetLevel) }" />
                  <span>Gate 1: Teoria</span>
                </div>
                <span
                  class="gate-badge"
                  :class="courseCompletion.completed ? 'gate-badge-complete' : 'gate-badge-outline'"
                >
                  {{ courseCompletion.completed ? 'Completo' : 'En curso' }}
                </span>
              </div>
              <div class="progress-gate-bars">
                <div class="progress-bar-item">
                  <div class="progress-bar-labels">
                    <span>Lecciones</span>
                    <span class="progress-bar-pct">{{ courseCompletion.lessonsPct }}%</span>
                  </div>
                  <q-linear-progress
                    :value="courseCompletion.lessonsPct / 100"
                    color="amber"
                    track-color="grey-9"
                    rounded
                    size="6px"
                  />
                </div>
                <div class="progress-bar-item">
                  <div class="progress-bar-labels">
                    <span>Quizzes</span>
                    <span class="progress-bar-pct">{{ courseCompletion.quizzesPct }}%</span>
                  </div>
                  <q-linear-progress
                    :value="courseCompletion.quizzesPct / 100"
                    color="green"
                    track-color="grey-9"
                    rounded
                    size="6px"
                  />
                </div>
              </div>
            </div>

            <!-- Exam CTA -->
            <div class="progress-exam">
              <div class="progress-exam-info">
                <p class="progress-exam-title">Examen de certificacion</p>
                <p class="progress-exam-desc">
                  50 preguntas &middot; 90 min &middot; {{ getPassingPct(targetLevel) }}% para aprobar
                </p>
              </div>
              <span
                v-if="hasPassedExam(targetLevel)"
                class="gate-badge gate-badge-gold"
              >
                Aprobado
              </span>
              <span
                v-else-if="!courseCompletion.completed"
                class="gate-badge gate-badge-outline"
              >
                <q-icon name="lock" size="12px" class="q-mr-xs" />
                Completa el curso
              </span>
              <router-link
                v-else
                :to="`/quiz/cert-${targetLevel}`"
                class="exam-cta-btn"
                :style="{ background: getTierColor(targetLevel) }"
              >
                Rendir examen
              </router-link>
            </div>

            <!-- Gate 2: Clase Especial -->
            <div class="progress-gate-2">
              <div class="progress-gate-2-info">
                <div class="progress-gate-title">
                  <q-icon name="school" size="16px" :style="{ color: 'var(--oa-text-muted)' }" />
                  <span>Gate 2: Clase Especial</span>
                </div>
                <p class="progress-gate-2-desc">
                  Evaluacion en vivo por panel &middot; Rubrica hexagonal &middot; Min 6/10
                </p>
              </div>
              <span class="gate-badge gate-badge-outline">
                <q-icon name="lock" size="12px" class="q-mr-xs" />
                Aprobar examen primero
              </span>
            </div>
          </div>
        </div>

        <!-- Dual Gate info -->
        <div class="cert-section">
          <div class="dual-gate-card">
            <div class="dual-gate-item">
              <div class="dual-gate-header">
                <q-icon name="description" size="14px" :style="{ color: 'var(--oa-gold-full)' }" />
                <span class="dual-gate-label">Gate 1: Examen Teorico</span>
              </div>
              <p class="dual-gate-desc">50 preguntas &middot; 90 min &middot; 3 intentos &middot; Autocorregible</p>
            </div>
            <div class="dual-gate-item">
              <div class="dual-gate-header">
                <q-icon name="school" size="14px" :style="{ color: '#4ade80' }" />
                <span class="dual-gate-label">Gate 2: Clase Especial</span>
              </div>
              <p class="dual-gate-desc">Panel en vivo &middot; Rubrica hexagonal &middot; Min 6/10 por dimension</p>
            </div>
          </div>
        </div>

        <!-- My Certificates -->
        <div v-if="certificates.length > 0" class="cert-section">
          <h2 class="cert-section-title">Mis Certificados</h2>
          <div class="certs-list">
            <div
              v-for="cert in certificates"
              :key="cert.id"
              class="cert-card"
              :style="{
                border: '1px solid ' + (cert.status === 'certificado' ? getTierColor(cert.level) : 'var(--oa-border)'),
                boxShadow: cert.status === 'certificado'
                  ? '0 0 16px color-mix(in srgb, ' + getTierColor(cert.level) + ' 8%, transparent)'
                  : undefined,
              }"
            >
              <div class="cert-card-left">
                <div
                  class="cert-card-icon"
                  :style="{
                    background: 'color-mix(in srgb, ' + getTierColor(cert.level) + ' 12%, transparent)',
                    color: getTierColor(cert.level),
                  }"
                >
                  <q-icon :name="getStatusIcon(cert.status)" size="20px" />
                </div>
                <div>
                  <p class="cert-card-title">{{ LEVEL_LABELS[cert.level] || cert.level }}</p>
                  <p class="cert-card-number">{{ cert.certNumber }}</p>
                  <p v-if="cert.issuedAt" class="cert-card-dates">
                    {{ formatDate(cert.issuedAt) }}
                    <template v-if="cert.expiresAt"> — Vence: {{ formatDate(cert.expiresAt) }}</template>
                  </p>
                </div>
              </div>
              <div class="cert-card-right">
                <span class="cert-status-badge" :class="'cert-status-' + cert.status">
                  {{ getStatusLabel(cert.status) }}
                </span>
                <router-link
                  v-if="cert.status === 'certificado'"
                  :to="`/verificar/${cert.certNumber}`"
                  class="cert-verify-link"
                  title="Ver certificado publico"
                >
                  <q-icon name="open_in_new" size="16px" />
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent Exams -->
        <div v-if="exams.length > 0" class="cert-section">
          <h2 class="cert-section-title">Examenes Recientes</h2>
          <div class="exams-card">
            <div
              v-for="(exam, i) in exams"
              :key="exam.id"
              class="exam-row"
              :class="{ 'exam-row-border': i < exams.length - 1 }"
            >
              <div class="exam-row-left">
                <div
                  class="exam-level-badge"
                  :style="{
                    background: 'color-mix(in srgb, ' + getTierColor(exam.level) + ' 10%, transparent)',
                    color: getTierColor(exam.level),
                  }"
                >
                  {{ LEVEL_SHORT[exam.level] || exam.level.toUpperCase() }}
                </div>
                <div>
                  <p class="exam-row-title">
                    {{ LEVEL_LABELS[exam.level] || exam.level }}
                    <span class="exam-row-attempt"> — Intento #{{ exam.attemptNumber }}</span>
                  </p>
                  <p class="exam-row-date">
                    {{ exam.completedAt ? formatDate(exam.completedAt) : 'En curso' }}
                    &middot;
                    <span class="exam-row-score">{{ exam.scorePct }}%</span>
                  </p>
                </div>
              </div>
              <span class="cert-status-badge" :class="exam.passed ? 'cert-status-certificado' : 'cert-status-outline'">
                {{ exam.passed ? 'Aprobado' : exam.status === 'in_progress' ? 'En curso' : 'No aprobado' }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { api } from 'boot/axios';

// ── Types ──

interface Cert {
  id: string;
  level: string;
  certNumber: string;
  status: string;
  issuedAt: string | null;
  expiresAt: string | null;
}

interface Exam {
  id: string;
  level: string;
  status: string;
  attemptNumber: number;
  scorePct: number;
  passed: boolean;
  completedAt: string | null;
  cooldownUntil: string | null;
}

interface CourseCompletion {
  completed: boolean;
  lessonsPct: number;
  quizzesPct: number;
}

// ── Constants ──

const LEVEL_LABELS: Record<string, string> = {
  cc: 'Coach Certified',
  sc: 'Senior Coach',
  c1: 'Coach Instructor',
  c2: 'Lead Trainer',
  ot_1: 'Olympic Trainer',
  owner: 'Owner/Partner',
  oa: 'Olympic Auditor',
  gj: 'Games Judge',
  kairos: 'Kairos',
};

const LEVEL_SHORT: Record<string, string> = {
  cc: 'CC', sc: 'SC', c1: 'C1', c2: 'C2',
  ot_1: 'OT', owner: 'OWN', oa: 'OA', gj: 'GJ', kairos: 'KA',
};

const TIER_COLORS: Record<string, string> = {
  cc: '#4ade80', sc: '#60a5fa', c1: '#a78bfa',
  c2: '#fbbf24', ot_1: '#fb923c', owner: '#f472b6',
  oa: '#2dd4bf', gj: '#f87171', kairos: '#c084fc',
};

const CAREER_TRACKS: Record<string, {
  label: string;
  path: Array<{ key: string; passingPct: number }>;
  prereq?: string;
  standalone?: boolean;
}> = {
  formativo: {
    label: 'Track Formativo',
    path: [
      { key: 'cc', passingPct: 70 },
      { key: 'sc', passingPct: 70 },
      { key: 'c1', passingPct: 80 },
      { key: 'c2', passingPct: 80 },
    ],
  },
  operativo: {
    label: 'Track Operativo',
    path: [
      { key: 'ot_1', passingPct: 75 },
      { key: 'owner', passingPct: 80 },
    ],
    prereq: 'SC',
  },
  especializaciones: {
    label: 'Especializaciones',
    path: [
      { key: 'oa', passingPct: 80 },
      { key: 'kairos', passingPct: 70 },
      { key: 'gj', passingPct: 70 },
    ],
    standalone: true,
  },
};

const STATUS_CONFIG: Record<string, { icon: string; label: string }> = {
  certificado: { icon: 'check_circle', label: 'Certificado' },
  en_progreso: { icon: 'schedule', label: 'En progreso' },
  requisitos_cumplidos: { icon: 'emoji_events', label: 'Requisitos cumplidos' },
  expirado: { icon: 'cancel', label: 'Expirado' },
  en_gracia: { icon: 'schedule', label: 'En gracia' },
  revocado: { icon: 'cancel', label: 'Revocado' },
};

// ── State ──

const loading = ref(true);
const error = ref<string | null>(null);
const role = ref('');
const careerStage = ref('');
const xp = ref(0);
const certificates = ref<Cert[]>([]);
const exams = ref<Exam[]>([]);
const courseCompletion = ref<CourseCompletion | null>(null);
const targetLevel = ref<string | null>(null);

// ── Computed ──

const certsByLevel = computed(() =>
  new Map(certificates.value.map((c) => [c.level, c])),
);

const certCount = computed(() =>
  certificates.value.filter((c) => c.status === 'certificado').length,
);

const passedExams = computed(() =>
  exams.value.filter((e) => e.passed).length,
);

// ── Methods ──

function getTierColor(level: string): string {
  return TIER_COLORS[level] ?? 'var(--oa-gold-dim)';
}

function isCertified(levelKey: string): boolean {
  const cert = certsByLevel.value.get(levelKey);
  return cert?.status === 'certificado';
}

function getStepStyle(levelKey: string): Record<string, string> {
  const color = getTierColor(levelKey);
  const certified = isCertified(levelKey);
  const isCurrent = targetLevel.value === levelKey;
  return {
    background: certified
      ? `color-mix(in srgb, ${color} 20%, transparent)`
      : isCurrent
        ? `color-mix(in srgb, ${color} 12%, transparent)`
        : 'var(--oa-bg-3)',
    color: certified || isCurrent ? color : 'var(--oa-text-disabled)',
    boxShadow: isCurrent ? `0 0 0 2px ${color}` : 'none',
    borderWidth: certified ? '2px' : '1px',
    borderColor: certified ? color : 'transparent',
    borderStyle: 'solid',
  };
}

function getStepLabelColor(levelKey: string): string {
  const color = getTierColor(levelKey);
  if (isCertified(levelKey)) return color;
  if (targetLevel.value === levelKey) return color;
  return 'var(--oa-text-disabled)';
}

function getPassingPct(level: string): number {
  for (const track of Object.values(CAREER_TRACKS)) {
    const found = track.path.find((p) => p.key === level);
    if (found) return found.passingPct;
  }
  return 70;
}

function hasPassedExam(level: string): boolean {
  return exams.value.some((e) => e.level === level && e.passed);
}

function getStatusIcon(status: string): string {
  return STATUS_CONFIG[status]?.icon ?? 'schedule';
}

function getStatusLabel(status: string): string {
  return STATUS_CONFIG[status]?.label ?? 'En progreso';
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

async function loadCareerData() {
  loading.value = true;
  error.value = null;
  try {
    const [careerRes, certsRes] = await Promise.all([
      api.get('/certification/career'),
      api.get('/certification/mine'),
    ]);

    const career = careerRes.data;
    role.value = career.role ?? '';
    careerStage.value = career.careerStage ?? '';
    xp.value = career.xp ?? 0;
    courseCompletion.value = career.courseCompletion ?? null;
    targetLevel.value = career.targetLevel ?? null;
    exams.value = career.exams ?? [];
    certificates.value = certsRes.data ?? [];
  } catch (err) {
    console.error('[certificacion] loadCareerData error:', err);
    error.value = 'Error al cargar datos de certificacion';
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  loadCareerData();
});
</script>

<style scoped>
.certificacion-page {
  padding: 16px;
}

.cert-container {
  max-width: 900px;
  margin: 0 auto;
}

.cert-loading {
  padding: 24px;
}

.cert-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 80px 24px;
}

.empty-text {
  color: var(--oa-text-muted);
  font-size: 14px;
}

/* Header */
.cert-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.cert-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.cert-subtitle {
  font-size: 12px;
  color: var(--oa-text-muted);
  margin: 2px 0 0 0;
}

.cert-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cert-header-stats {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  color: var(--oa-text-muted);
}

.stat-value {
  font-family: monospace;
  color: var(--oa-text-primary);
}

.stat-xp {
  font-family: monospace;
  color: var(--oa-gold-full);
}

.ascenso-link {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 500;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  color: var(--oa-gold-full);
  text-decoration: none;
}

/* Sections */
.cert-section {
  margin-bottom: 20px;
}

.cert-section-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0 0 16px 0;
}

/* Track cards */
.tracks-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.track-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  padding: 16px;
}

.track-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.track-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--oa-text-muted);
}

.track-prereq {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--oa-bg-3);
  color: var(--oa-text-disabled);
}

.track-path {
  display: flex;
  align-items: center;
  gap: 4px;
}

.track-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.track-step-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  transition: all 0.3s;
}

.step-pulse {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.step-short {
  font-size: 12px;
  font-weight: 700;
}

.track-step-label {
  font-size: 10px;
  font-weight: 500;
  text-align: center;
  line-height: 1.2;
  margin: 6px 0 0 0;
}

.track-step-pct {
  font-size: 9px;
  color: var(--oa-text-disabled);
  margin: 2px 0 0 0;
}

.track-chevron {
  flex-shrink: 0;
  margin: 0 2px;
  margin-bottom: 24px;
}

/* Progress card */
.progress-card {
  background: var(--oa-bg-2);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.progress-gate {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.progress-gate-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.progress-gate-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--oa-text-primary);
}

.progress-gate-bars {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.progress-bar-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.progress-bar-labels {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: var(--oa-text-muted);
}

.progress-bar-pct {
  font-family: monospace;
}

.progress-exam {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--oa-border);
  padding-top: 16px;
}

.progress-exam-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.progress-exam-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
}

.progress-exam-desc {
  font-size: 11px;
  color: var(--oa-text-muted);
  margin: 0;
}

.exam-cta-btn {
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  color: var(--color-marble-cream);
  text-decoration: none;
  transition: filter 0.2s;
}

.exam-cta-btn:hover {
  filter: brightness(1.1);
}

.progress-gate-2 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--oa-border);
  padding-top: 16px;
}

.progress-gate-2-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.progress-gate-2-desc {
  font-size: 11px;
  color: var(--oa-text-muted);
  margin: 2px 0 0 24px;
}

/* Gate badges */
.gate-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.gate-badge-complete {
  background: color-mix(in srgb, #4ade80 15%, transparent);
  color: #4ade80;
  border: 1px solid color-mix(in srgb, #4ade80 30%, transparent);
}

.gate-badge-outline {
  background: var(--oa-bg-2);
  color: var(--oa-text-muted);
  border: 1px solid var(--oa-border);
}

.gate-badge-gold {
  background: color-mix(in srgb, var(--oa-gold-full) 15%, transparent);
  color: var(--oa-gold-full);
  border: 1px solid var(--oa-gold-dim);
}

/* Dual gate info */
.dual-gate-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  padding: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.dual-gate-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dual-gate-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dual-gate-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--oa-text-primary);
}

.dual-gate-desc {
  font-size: 11px;
  color: var(--oa-text-muted);
  line-height: 1.5;
  margin: 0;
}

/* Certificate cards */
.certs-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cert-card {
  background: var(--oa-bg-2);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.cert-card-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cert-card-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cert-card-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.cert-card-number {
  font-size: 10px;
  font-family: monospace;
  color: var(--oa-text-muted);
  margin: 2px 0 0 0;
}

.cert-card-dates {
  font-size: 10px;
  color: var(--oa-text-disabled);
  margin: 2px 0 0 0;
}

.cert-card-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cert-verify-link {
  display: flex;
  align-items: center;
  padding: 6px;
  border-radius: 8px;
  color: var(--oa-text-muted);
  transition: color 0.2s;
}

.cert-verify-link:hover {
  color: var(--oa-gold-full);
}

/* Status badges */
.cert-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.cert-status-certificado {
  background: color-mix(in srgb, var(--oa-gold-full) 15%, transparent);
  color: var(--oa-gold-full);
  border: 1px solid var(--oa-gold-dim);
}

.cert-status-en_progreso {
  background: color-mix(in srgb, #60a5fa 15%, transparent);
  color: #60a5fa;
  border: 1px solid color-mix(in srgb, #60a5fa 30%, transparent);
}

.cert-status-requisitos_cumplidos {
  background: color-mix(in srgb, #fbbf24 15%, transparent);
  color: #fbbf24;
  border: 1px solid color-mix(in srgb, #fbbf24 30%, transparent);
}

.cert-status-expirado {
  background: color-mix(in srgb, #ef4444 15%, transparent);
  color: #ef4444;
  border: 1px solid color-mix(in srgb, #ef4444 30%, transparent);
}

.cert-status-en_gracia {
  background: color-mix(in srgb, #fb923c 15%, transparent);
  color: #fb923c;
  border: 1px solid color-mix(in srgb, #fb923c 30%, transparent);
}

.cert-status-revocado {
  background: var(--oa-bg-3);
  color: var(--oa-text-disabled);
  border: 1px solid var(--oa-border);
}

.cert-status-outline {
  background: var(--oa-bg-2);
  color: var(--oa-text-muted);
  border: 1px solid var(--oa-border);
}

/* Exam rows */
.exams-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  overflow: hidden;
}

.exam-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
}

.exam-row-border {
  border-bottom: 1px solid var(--oa-border);
}

.exam-row-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.exam-level-badge {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
}

.exam-row-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
}

.exam-row-attempt {
  font-weight: 400;
  color: var(--oa-text-muted);
}

.exam-row-date {
  font-size: 11px;
  color: var(--oa-text-disabled);
  margin: 2px 0 0 0;
}

.exam-row-score {
  font-family: monospace;
}

/* Mobile responsive */
@media (max-width: 640px) {
  .certificacion-page {
    padding: 8px;
  }

  .cert-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .cert-header-stats {
    flex-wrap: wrap;
    gap: 8px;
  }

  .track-path {
    flex-wrap: wrap;
    justify-content: center;
  }

  .track-chevron {
    display: none;
  }

  .dual-gate-card {
    grid-template-columns: 1fr;
  }

  .progress-gate-bars {
    grid-template-columns: 1fr;
  }
}
</style>
